import React from 'react';
import styled, { css } from 'styled-components';

import { CommonTheme } from 'app/shared/theme';
import { useButtonClickBehavior } from 'app/shared/utils/useButtonClickBehavior';
import {
  withPositionCSS,
  WithPositionProps,
  withSpacingCSS,
  WithSpacingProps,
  withZIndexCSS,
  WithZIndexProps,
} from 'app/shared/components/styleUtils';
import { Icon, IconCSSProps, IconProps } from 'app/shared/components/atoms';

export interface IconButtonProps
  extends React.ComponentPropsWithoutRef<'button'>,
    React.AriaAttributes,
    IconProps,
    WithPositionProps,
    WithZIndexProps,
    WithSpacingProps {}

interface IconButtonCSSProps
  extends IconCSSProps,
    WithPositionProps,
    WithZIndexProps,
    WithSpacingProps {
  theme: CommonTheme;
}

const StyledButton = styled.button<IconButtonCSSProps>`
  ${({ disabled = false, backgroundColor }) => css`
    border: none;
    ${backgroundColor && `background-color: ${backgroundColor}`};
    padding: 0;
    margin: 0;
    cursor: pointer;
    height: fit-content;
    width: fit-content;

    ${
      disabled &&
      `
      border: none;
      background-color: unset;
      cursor: auto;
      pointer-events: none;
    `
    }
  `}

  ${withPositionCSS};
  ${withSpacingCSS};
  ${withZIndexCSS};
`;

export const IconButton: React.FC<React.PropsWithChildren<IconButtonProps>> = ({
  position,
  top,
  right,
  disabled,
  onClick,
  p,
  pt,
  pr,
  pb,
  pl,
  m,
  mt,
  mr,
  mb,
  ml,
  backgroundColor,
  float,
  zIndex,
  'data-qaid': dataQaid,
  ...props
}) => {
  const { buttonRef, handleClick } = useButtonClickBehavior({ onClick });
  const spacingProps = { p, pt, pr, pb, pl, m, mt, mr, mb, ml };
  const stylingProps = { backgroundColor, float, zIndex };

  return (
    <StyledButton
      ref={buttonRef}
      onClick={handleClick}
      position={position}
      top={top}
      right={right}
      disabled={disabled}
      data-qaid={dataQaid}
      {...stylingProps}
      {...spacingProps}
    >
      <Icon p={0} m={0} {...props} data-qaid="icon-button-icon" />
    </StyledButton>
  );
};
